import { useState, useContext, useEffect } from "react";
import { Bell, CheckCheck, Mail, MailOpen, MessageSquare, Flag, Wrench, Folder, Volume2 } from "lucide-react";
import { SoundSelector } from "./sound-selector";
import { PulseReactionBar } from "@/components/pulse-reactions";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { formatDistanceToNow, format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useExpansion } from "@/contexts/expansion-context";
import { DialogContext } from "@/contexts/dialog-context";
import { useAuth } from "@/contexts/auth-context";
interface Notification {
  id: number;
  userId: number;
  pulseId: number;
  projectId: string;
  seen: boolean;
  seenAt: string | null;
  notificationType: string;
  tagMessage?: string;
  tagUrgent?: boolean;
  createdAt: string;
  createdBy: number | null;
  ownerUsername?: string;
  pulse: {
    id: number;
    pulseDescription: string;
    status: string;
    category: string;
    dateAdded: string;
  };
  project: {
    id: string;
    projectName: string;
    thumbnail?: string | null;
  };
}

interface NotificationCountResponse {
  count: number;
}

// Helper function to get category icon
const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'chat':
      return <MessageSquare className="w-4 h-4" />;
    case 'email_in':
      return <Mail className="w-4 h-4" />;
    case 'email_out':
      return <MailOpen className="w-4 h-4" />;
    case 'milestone':
      return <Flag className="w-4 h-4" />;
    case 'technical':
      return <Wrench className="w-4 h-4" />;
    case 'internal':
    default:
      return <Folder className="w-4 h-4" />;
  }
};

export function NotificationBell() {
  const { toast } = useToast();
  const { expandMultiple, expandedItems } = useExpansion();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [isOpen, setIsOpen] = useState(false);
  const [pendingDialog, setPendingDialog] = useState<{
    pulseId: number;
    requiredExpansions: string[];
    isChat: boolean;
    projectId: string;
    pulseDateAdded?: string;
  } | null>(null);
  const dialogContext = useContext(DialogContext);
  
  if (!dialogContext) {
    throw new Error('NotificationBell must be used within a DialogProvider');
  }
  
  const { setDialogOpen } = dialogContext;
  
  // Fetch user preferences for admin toggle state
  const { data: userPrefs } = useQuery<{ adminAllPulseNotifications?: boolean }>({
    queryKey: ['/api/user/preferences'],
    enabled: isAdmin,
  });
  
  // Mutation to toggle admin all pulse notifications
  const toggleAdminAllPulseMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiRequest('PATCH', '/api/user/preferences', {
        adminAllPulseNotifications: enabled,
      });
      if (!response.ok) {
        throw new Error('Failed to update preference');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/preferences'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/count'] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update notification preference",
        variant: "destructive",
      });
    },
  });

  // Watch for expansion completion and open dialog when ready
  useEffect(() => {
    if (!pendingDialog) return;

    const allExpanded = pendingDialog.requiredExpansions.every(
      id => expandedItems[id]
    );

    if (allExpanded) {
      if (pendingDialog.isChat) {
        // For chat pulses, dispatch custom event to open chat with timestamp
        const event = new CustomEvent('open-chat-pulse', {
          detail: {
            projectId: pendingDialog.projectId,
            pulseId: pendingDialog.pulseId,
            pulseDateAdded: pendingDialog.pulseDateAdded
          }
        });
        window.dispatchEvent(event);
      } else {
        // For regular pulses, open the pulse detail dialog
        setDialogOpen(`pulse-detail-${pendingDialog.pulseId}`, true);
      }
      setPendingDialog(null);
    }
  }, [expandedItems, pendingDialog, setDialogOpen]);
  
  // Fetch unseen notification count
  const { data: countData, refetch: refetchCount } = useQuery<NotificationCountResponse>({
    queryKey: ['/api/notifications/count'],
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Fetch notifications (both seen and unseen, limit 20)
  const { data: notifications = [], isLoading, refetch: refetchNotifications } = useQuery<Notification[]>({
    queryKey: ['/api/notifications'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/notifications?limit=20');
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }
      return response.json();
    },
    enabled: isOpen, // Only fetch when dropdown is open
  });

  // Mark individual notification as seen
  const markAsSeenMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await apiRequest('PATCH', `/api/notifications/${notificationId}/seen`);
      if (!response.ok) {
        throw new Error('Failed to mark notification as seen');
      }
      return response.json();
    },
    onSuccess: (data) => {
      if (data?.reason === 'not_owner') {
        toast({
          title: "Info",
          description: "This notification belongs to another user",
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/count'] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to mark notification as seen",
        variant: "destructive",
      });
    },
  });

  // Mark individual notification as unseen
  const markAsUnseenMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await apiRequest('PATCH', `/api/notifications/${notificationId}/unseen`);
      if (!response.ok) {
        throw new Error('Failed to mark notification as unseen');
      }
      return response.json();
    },
    onSuccess: (data) => {
      if (data?.reason === 'not_owner') {
        toast({
          title: "Info",
          description: "This notification belongs to another user",
        });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/count'] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to mark notification as unseen",
        variant: "destructive",
      });
    },
  });

  // Mark all pulse notifications as seen
  const markAllAsSeenMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('PATCH', '/api/notifications/mark-all-seen', {});
      if (!response.ok) {
        throw new Error('Failed to mark all notifications as seen');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/count'] });
      toast({
        title: "Success",
        description: "All pulse notifications marked as seen",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to mark all notifications as seen",
        variant: "destructive",
      });
    },
  });

  // Mark all chat messages in all projects as read (panic button)
  const markAllChatsAsReadMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/chat/mark-all-read', {});
      if (!response.ok) {
        throw new Error('Failed to mark all chats as read');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat/unread-counts-all'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/count'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      toast({
        title: "Success",
        description: "All chats marked as read",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to mark all chats as read",
        variant: "destructive",
      });
    },
  });

  // Fetch unread chat counts for all projects (batched endpoint - includes firstUnreadMessageId for scroll-to)
  const { data: chatUnreadData = [], isLoading: isLoadingChats, refetch: refetchChatCounts } = useQuery<Array<{ projectId: string; projectName: string; unreadCount: number; firstUnreadMessageId: number | null; channelId?: string; channelType?: string; }>>({
    queryKey: ['/api/chat/unread-counts-all'],
    refetchInterval: 30000, // Refetch every 30 seconds to keep badge current
    enabled: isOpen, // Only fetch when dropdown is open (badge count is handled by backend)
  });

  // WebSocket pulse creation events are handled by the WebSocketProvider context
  // which automatically invalidates notification queries when pulses are created

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.seen) {
      markAsSeenMutation.mutate(notification.id);
    }
    
    const shortYear = notification.projectId.split('-')[0];
    const requiredExpansions = [
      `year-${shortYear}`,
      `project-${notification.projectId}`,
      `pulse-${notification.pulseId}`
    ];
    
    expandMultiple(requiredExpansions);
    setPendingDialog({
      pulseId: notification.pulseId,
      requiredExpansions,
      isChat: notification.pulse.category === 'chat',
      projectId: notification.projectId,
      pulseDateAdded: notification.pulse.dateAdded
    });
    
    setIsOpen(false);
  };

  const handleToggleSeen = (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation(); // Prevent notification click
    if (notification.seen) {
      markAsUnseenMutation.mutate(notification.id);
    } else {
      markAsSeenMutation.mutate(notification.id);
    }
  };

  // Filter out orphaned notifications (where pulse has been deleted) and sort
  // Sort: unseen first, then by creation date
  const sortedNotifications = [...notifications]
    .filter(notification => notification.pulse != null) // Remove orphaned notifications
    .sort((a, b) => {
      if (a.seen !== b.seen) {
        return a.seen ? 1 : -1; // Unseen first
      }
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  const handleChatClick = (projectId: string, firstUnreadMessageId: number | null, channelId?: string, channelType?: string) => {
    const event = new CustomEvent('open-chat-pulse', {
      detail: {
        projectId,
        pulseId: null,
        pulseDateAdded: null,
        targetMessageId: firstUnreadMessageId,
        channelId,
        channelType,
      }
    });
    window.dispatchEvent(event);
    setIsOpen(false);
  };

  // Badge count is now handled entirely by backend (pulse + chat project counts combined)
  const totalUnreadCount = countData?.count || 0;

  // Handle dropdown open/close with manual refetch as fallback
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    
    // When opening the dropdown, manually refetch all queries to ensure fresh data
    // This serves as a robust fallback in addition to WebSocket invalidations
    if (newOpen) {
      refetchCount();
      refetchNotifications();
      refetchChatCounts();
    }
  };

  return (
    <>
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="relative navy-card border-gray-600 text-gray-300 hover:bg-gray-700"
          data-testid="button-notification-bell"
        >
          <Bell className="w-4 h-4" />
          {totalUnreadCount > 0 && (
            <span 
              className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-[#FF8C00] rounded-full"
              data-testid="badge-notification-count"
            >
              {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-[500px] bg-gray-800 border-gray-600 max-h-[780px] overflow-y-auto"
      >
        <DropdownMenuLabel className="flex items-center justify-between text-gray-300">
          <div className="flex items-center gap-2">
            <span>Pulse Notifications</span>
            <SoundSelector soundType="soundPulseNotifications" label="" />
            {isAdmin && (
              <div 
                className="flex items-center gap-1.5 ml-2 px-2 py-0.5 rounded bg-gray-700/50 border border-gray-600"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <span className="text-[10px] text-gray-400 whitespace-nowrap">All to Admin</span>
                <Switch
                  checked={userPrefs?.adminAllPulseNotifications ?? false}
                  onCheckedChange={(checked) => toggleAdminAllPulseMutation.mutate(checked)}
                  disabled={toggleAdminAllPulseMutation.isPending}
                  className="h-4 w-7 data-[state=checked]:bg-orange-500"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </div>
          {notifications.some(n => !n.seen) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-gray-400 hover:text-gray-200"
              onClick={() => markAllAsSeenMutation.mutate()}
              disabled={markAllAsSeenMutation.isPending}
              data-testid="button-mark-all-seen"
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Mark All as Seen
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-gray-700" />
        
        {isLoading ? (
          <div className="p-4 text-center text-gray-400">
            Loading notifications...
          </div>
        ) : sortedNotifications.length === 0 ? (
          <div className="p-4 text-center text-gray-400">
            No notifications
          </div>
        ) : (
          <div className="max-h-[480px] overflow-y-auto">
            {sortedNotifications.map((notification, index) => {
              // Extract first line of pulse description
              const firstLine = notification.pulse?.pulseDescription?.split('\n')[0] || 'New pulse';
              const truncatedDescription = firstLine.length > 60 
                ? firstLine.substring(0, 60) + '...' 
                : firstLine;
              
              // Check if project changed from previous notification
              const showThumbnail = index === 0 || sortedNotifications[index - 1].projectId !== notification.projectId;

              return (
                <div
                  key={notification.id}
                  className={cn(
                    "flex items-start gap-3 px-3 py-3 cursor-default rounded-sm",
                    !notification.seen 
                      ? "bg-[#FF8C00]/10 border-l-2 border-[#FF8C00]" 
                      : "text-gray-400"
                  )}
                  data-testid={`notification-item-${notification.id}`}
                >
                  <div
                    className="flex items-start gap-3 flex-1 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    {showThumbnail && (
                      <div className="flex-shrink-0 w-12 h-6 bg-gray-700 rounded overflow-hidden">
                        {notification.project?.thumbnail ? (
                          <img 
                            src={notification.project.thumbnail} 
                            alt={notification.project.projectName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">
                            {notification.projectId.split('-')[1]?.substring(0, 2) || '--'}
                          </div>
                        )}
                      </div>
                    )}
                    {!showThumbnail && <div className="flex-shrink-0 w-12" />}
                    
                    <div className="flex-shrink-0 mt-1" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={notification.seen}
                        onCheckedChange={() => {
                          handleToggleSeen(
                            { stopPropagation: () => {} } as React.MouseEvent, 
                            notification
                          );
                        }}
                        className="border-gray-500"
                        data-testid={`checkbox-mark-seen-${notification.id}`}
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className={cn(
                            "text-xs font-medium",
                            !notification.seen ? "text-[#FF8C00]" : "text-gray-500"
                          )}>
                            {notification.projectId} - {notification.project?.projectName || 'Unknown'}
                          </span>
                          {notification.ownerUsername && notification.userId !== user?.id && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-600/60 text-gray-400 whitespace-nowrap flex-shrink-0">
                              {notification.ownerUsername}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className={cn(
                          "flex-shrink-0 mt-0.5",
                          !notification.seen ? "text-[#FF8C00]" : "text-gray-500"
                        )}>
                          {getCategoryIcon(notification.pulse?.category || 'internal')}
                        </span>
                        <p className={cn(
                          "text-sm line-clamp-2 flex-1",
                          !notification.seen ? "text-gray-200" : "text-gray-400"
                        )}>
                          {truncatedDescription}
                        </p>
                      </div>
                      {notification.tagMessage && (
                        <p className="mt-2 rounded border border-sky-600/30 bg-sky-950/40 px-2 py-1.5 text-sm font-semibold text-sky-100 whitespace-pre-wrap break-words">
                          {notification.tagUrgent && <span className="text-amber-300">Urgent · </span>}
                          {notification.tagMessage}
                        </p>
                      )}
                    </div>
                  </div>

                  <div
                    className="flex-shrink-0 flex items-center px-2 self-stretch"
                    onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <PulseReactionBar
                      pulseId={notification.pulseId}
                      projectId={notification.projectId}
                      className="h-4 w-4"
                      side="left"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Tagged Notifications section - Unread chats with @mention support */}
        <DropdownMenuSeparator className="bg-gray-700 my-2" />
        <DropdownMenuLabel className="flex items-center justify-between text-gray-300">
          <div className="flex items-center gap-2">
            <span>Tagged Notifications ({chatUnreadData.length})</span>
            <SoundSelector soundType="soundMentions" label="@" />
          </div>
          {chatUnreadData.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-gray-400 hover:text-gray-200"
              onClick={() => markAllChatsAsReadMutation.mutate()}
              disabled={markAllChatsAsReadMutation.isPending}
              data-testid="button-mark-all-chats-read"
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Mark All Read
            </Button>
          )}
        </DropdownMenuLabel>
        
        {isLoadingChats ? (
          <div className="p-4 text-center text-gray-400">
            Loading chats...
          </div>
        ) : chatUnreadData.length === 0 ? (
          <div className="p-4 text-center text-gray-400">
            No tagged notifications
          </div>
        ) : (
          <div className="max-h-[280px] overflow-y-auto">
            {chatUnreadData.map((item) => (
              <DropdownMenuItem
                key={item.projectId}
                className="flex items-center justify-between p-3 cursor-pointer bg-blue-500/10 hover:bg-blue-500/20 border-l-2 border-blue-500"
                onSelect={(e) => {
                  e.preventDefault();
                  handleChatClick(item.projectId, item.firstUnreadMessageId, item.channelId, item.channelType);
                }}
                data-testid={`chat-unread-${item.projectId}`}
              >
                <div className="flex-1">
                  <span className="text-sm font-medium text-blue-400">
                    {item.projectName}
                  </span>
                  <p className="text-xs text-gray-400 mt-1">
                    {item.projectId} • {item.unreadCount} unread
                  </p>
                </div>
                <span className="flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-blue-500 rounded-full">
                  {item.unreadCount > 99 ? '99+' : item.unreadCount}
                </span>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  </>
  );
}
